import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/Button';
import { SettingsModal } from './SettingsModal';
import * as api from '../services/settingsApi';
import {
    UserCheck, Shield, Plus, Search, X, Loader2,
} from 'lucide-react';
import { clsx } from 'clsx';

interface AssignRoleModalProps {
    open: boolean;
    onClose: () => void;
}

export function AssignRoleModal({ open, onClose }: AssignRoleModalProps) {
    const tenantId = useAuthStore((s) => s.user?.tenantId);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [selectedRoleId, setSelectedRoleId] = useState('');

    // Fetch all staff
    const { data: staff, isLoading: staffLoading } = useQuery({
        queryKey: ['staff', tenantId],
        queryFn: () => api.getStaff(tenantId!),
        enabled: !!tenantId && open,
    });

    // Fetch all roles
    const { data: roles, isLoading: rolesLoading } = useQuery({
        queryKey: ['roles', tenantId],
        queryFn: () => api.getRoles(tenantId!),
        enabled: !!tenantId && open,
    });

    // Fetch selected user's roles
    const { data: userRoles, isLoading: userRolesLoading } = useQuery({
        queryKey: ['userRoles', tenantId, selectedUserId],
        queryFn: () => api.getUserRoles(tenantId!, selectedUserId!),
        enabled: !!tenantId && !!selectedUserId && open,
    });

    // Reset selected role when user changes
    useEffect(() => {
        setSelectedRoleId('');
    }, [selectedUserId]);

    // Assign role mutation
    const assignMutation = useMutation({
        mutationFn: () => api.assignRoleToUser(tenantId!, selectedUserId!, selectedRoleId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['userRoles', tenantId, selectedUserId] });
            queryClient.invalidateQueries({ queryKey: ['roles', tenantId] });
            addToast({ type: 'success', title: 'Role assigned successfully' });
            setSelectedRoleId('');
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Failed to assign role',
                message: err?.response?.data?.error?.message || 'An error occurred',
            });
        },
    });

    // Remove role mutation
    const removeMutation = useMutation({
        mutationFn: (roleId: string) => api.removeRoleFromUser(tenantId!, selectedUserId!, roleId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['userRoles', tenantId, selectedUserId] });
            queryClient.invalidateQueries({ queryKey: ['roles', tenantId] });
            addToast({ type: 'success', title: 'Role removed successfully' });
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Failed to remove role',
                message: err?.response?.data?.error?.message || 'An error occurred',
            });
        },
    });

    const filteredStaff = (staff || []).filter((s: any) => {
        const term = searchTerm.toLowerCase();
        return (
            (s.firstName || '').toLowerCase().includes(term) ||
            (s.lastName || '').toLowerCase().includes(term) ||
            (s.email || '').toLowerCase().includes(term)
        );
    });

    const selectedUser = (staff || []).find((s: any) => s.id === selectedUserId);
    const availableRoles = (roles || []).filter(
        (r: any) => !(userRoles || []).some((ur: any) => ur.id === r.id)
    );

    const isLoading = staffLoading || rolesLoading;

    return (
        <SettingsModal
            open={open}
            onClose={onClose}
            title="Assign Roles to Users"
            subtitle="Manage which roles each staff member has"
            icon={UserCheck}
            size="lg"
            footer={
                <Button variant="secondary" onClick={onClose}>
                    Done
                </Button>
            }
        >
            {isLoading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 size={32} className="animate-spin text-slate-300" />
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-[400px]">
                    {/* LEFT: Staff List */}
                    <div className="lg:col-span-1 border border-slate-200 rounded-xl overflow-hidden flex flex-col">
                        <div className="p-3 border-b border-slate-200 bg-slate-50">
                            <div className="relative">
                                <Search
                                    size={14}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                />
                                <input
                                    type="text"
                                    placeholder="Search staff..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:border-amber-500 focus:ring-1 focus:ring-amber-500/20 outline-none"
                                />
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {filteredStaff.length === 0 ? (
                                <p className="text-sm text-slate-400 text-center py-8">
                                    No staff members found
                                </p>
                            ) : (
                                filteredStaff.map((staffMember: any) => (
                                    <button
                                        key={staffMember.id}
                                        type="button"
                                        onClick={() => setSelectedUserId(staffMember.id)}
                                        className={clsx(
                                            'w-full text-left px-4 py-3 border-b border-slate-100 transition-colors hover:bg-slate-50',
                                            selectedUserId === staffMember.id
                                                ? 'bg-amber-50 border-l-2 border-l-amber-500'
                                                : 'border-l-2 border-l-transparent'
                                        )}
                                    >
                                        <p className="text-sm font-medium text-slate-700">
                                            {staffMember.firstName} {staffMember.lastName}
                                        </p>
                                        <p className="text-xs text-slate-400">{staffMember.email}</p>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>

                    {/* RIGHT: Role Management */}
                    <div className="lg:col-span-2">
                        {!selectedUserId ? (
                            <div className="flex flex-col items-center justify-center h-full text-slate-400 py-12">
                                <UserCheck size={48} className="mb-3 opacity-30" />
                                <p className="text-sm font-medium">Select a staff member</p>
                                <p className="text-xs">to manage their roles</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {/* Selected User Info */}
                                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                                    <p className="text-sm font-semibold text-slate-700">
                                        {selectedUser?.firstName} {selectedUser?.lastName}
                                    </p>
                                    <p className="text-xs text-slate-500">{selectedUser?.email}</p>
                                </div>

                                {/* Current Roles */}
                                <div>
                                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                        Current Roles
                                    </h4>
                                    {userRolesLoading ? (
                                        <Loader2 size={16} className="animate-spin text-slate-300" />
                                    ) : (userRoles || []).length === 0 ? (
                                        <p className="text-sm text-slate-400 italic">
                                            No roles assigned yet
                                        </p>
                                    ) : (
                                        <div className="flex flex-wrap gap-2">
                                            {(userRoles || []).map((role: any) => (
                                                <div
                                                    key={role.id}
                                                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700"
                                                >
                                                    <Shield size={14} />
                                                    <span className="font-medium">{role.name}</span>
                                                    {role.isSystemRole ? (
                                                        <span className="text-[10px] bg-amber-200 px-1.5 py-0.5 rounded text-amber-600">
                                                            System
                                                        </span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeMutation.mutate(role.id)}
                                                            disabled={removeMutation.isPending}
                                                            className="p-0.5 rounded hover:bg-amber-100 transition-colors text-amber-400 hover:text-amber-600 disabled:opacity-50"
                                                            title="Remove role"
                                                        >
                                                            <X size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Assign New Role */}
                                <div>
                                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                                        Assign New Role
                                    </h4>
                                    {availableRoles.length === 0 ? (
                                        <p className="text-sm text-slate-400 italic">
                                            All roles are already assigned
                                        </p>
                                    ) : (
                                        <div className="flex gap-2">
                                            <select
                                                value={selectedRoleId}
                                                onChange={(e) => setSelectedRoleId(e.target.value)}
                                                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500/20 outline-none"
                                            >
                                                <option value="">Select a role...</option>
                                                {availableRoles.map((role: any) => (
                                                    <option key={role.id} value={role.id}>
                                                        {role.name}
                                                        {role.isSystemRole ? ' (System)' : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            <Button
                                                size="sm"
                                                onClick={() => assignMutation.mutate()}
                                                isLoading={assignMutation.isPending}
                                                disabled={!selectedRoleId}
                                                leftIcon={<Plus size={14} />}
                                            >
                                                Assign
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </SettingsModal>
    );
}